<template>
  <p :class="$style.resendWrapper">
    {{ t('emailVerificationCodePanel.resendNote') }}
    <template v-if="remainingSeconds > 0">
      {{ ' ' }}{{ t('emailVerificationCodePanel.resend', { resendSeconds: remainingSeconds }) }}
    </template>
    <template v-else>
      {{ ' ' }}
      <HgLink
        tag="button"
        type="button"
        :class="[$style.resendLink, { [$style.isLoading]: isLoading }]"
        data-test="resend-email-verification-link"
        :aria-disabled="isLoading"
        @click="handleClick"
      >
        <span :class="$style.resendLinkContent">
          {{ t('emailVerificationCodePanel.resendNow') }}
          <LoadingSpinner v-if="isLoading" :class="$style.spinner" />
        </span>
      </HgLink>
    </template>
  </p>

  <HgToast
    v-if="isResendErrorToastVisible"
    data-test="resend-error-toast"
    :isVisible="isResendErrorToastVisible"
    @clear="isResendErrorToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-error-notification"
      :title="t('emailVerificationModal.resendError')"
      theme="danger"
    />
  </HgToast>

  <HgToast
    v-if="isResendAlreadyVerifiedToastVisible"
    data-test="resend-already-verified-toast"
    :isVisible="isResendAlreadyVerifiedToastVisible"
    @clear="isResendAlreadyVerifiedToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-already-verified-notification"
      :title="t('emailVerificationAlreadyVerified.message')"
      theme="info"
    >
      {{ t('emailVerificationAlreadyVerified.message2') }}
    </HgNotificationMessage>
  </HgToast>

  <HgToast
    v-if="isResendLimitReachedVisible"
    data-test="resend-limit-reached-toast"
    :isVisible="isResendLimitReachedVisible"
    @clear="isResendLimitReachedVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-limit-reached-notification"
      :title="t('emailVerificationModal.resendLimitError')"
      theme="warning"
    />
  </HgToast>

  <HgToast
    v-if="isResendSuccessToastVisible"
    data-test="resend-success-toast"
    :isVisible="isResendSuccessToastVisible"
    @clear="isResendSuccessToastVisible = false"
  >
    <HgNotificationMessage
      data-test="resend-success-notification"
      :title="t('emailVerificationModal.steps.openEmail', { brandName })"
      theme="success"
    />
  </HgToast>
</template>

<script setup lang="ts">
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { useContactFormStore } from '@/store/modules/contact-form';
import { HgLink, HgNotificationMessage, HgToast } from '@smg-real-estate/domus';
import { defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

defineOptions({
  name: 'ResendEmailVerificationButton',
});

const props = defineProps<{
  contactRequestId: string;
}>();

const emit = defineEmits<{
  click: [];
}>();

const { t } = useI18n();
const contactFormStore = useContactFormStore();
const LoadingSpinner = defineAsyncComponent(() => import('@/assets/loading_spinner.svg'));

const brandName = isPlatformIs24() ? 'ImmoScout24' : 'Homegate';
const RESEND_COOLDOWN_SECONDS = 60;
const remainingSeconds = ref(RESEND_COOLDOWN_SECONDS);
const isLoading = ref(false);
const isResendErrorToastVisible = ref(false);
const isResendAlreadyVerifiedToastVisible = ref(false);
const isResendLimitReachedVisible = ref(false);
const isResendSuccessToastVisible = ref(false);
let timerId: ReturnType<typeof setInterval> | undefined;

const clearCountdown = () => {
  if (!timerId) return;
  clearInterval(timerId);
  timerId = undefined;
};

const startCountdown = () => {
  clearCountdown();

  timerId = setInterval(() => {
    if (remainingSeconds.value <= 0) {
      clearCountdown();
      return;
    }

    remainingSeconds.value -= 1;
  }, 1000);
};

const handleClick = () => {
  if (isLoading.value || remainingSeconds.value > 0) return;

  void resendConfirmationLink();
};

// eslint-disable-next-line max-statements
const resendConfirmationLink = async () => {
  isLoading.value = true;
  clearCountdown();
  contactFormStore.emailVerificationResendStatus = undefined;

  emit('click');
  await contactFormStore.resendConfirmationLink(props.contactRequestId);

  isLoading.value = false;
  if (contactFormStore.emailVerificationResendStatus === 'OK') {
    isResendSuccessToastVisible.value = true;
    remainingSeconds.value = RESEND_COOLDOWN_SECONDS;
    startCountdown();
    return;
  }

  if (contactFormStore.emailVerificationResendStatus === 'EMAIL_ALREADY_VERIFIED') {
    isResendAlreadyVerifiedToastVisible.value = true;
    remainingSeconds.value = 0;
    return;
  }

  if (contactFormStore.emailVerificationResendStatus === 'RESEND_COUNT_THRESHOLD_REACHED') {
    isResendLimitReachedVisible.value = true;
    remainingSeconds.value = 0;
    return;
  }

  isResendErrorToastVisible.value = true;
  remainingSeconds.value = 0;
};

onMounted(() => {
  startCountdown();
});

onUnmounted(() => {
  clearCountdown();
});
</script>

<style lang="scss" module>
@import '@/assets/variables';

.resendWrapper {
  @include body-3;

  margin: 0;
  color: $color-grey-100;
  text-align: center;
  font-size: 12px;
}

.resendLink {
  display: inline;
  padding: 0;
  color: var(--base-color-link);
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  text-decoration: underline;
  vertical-align: baseline;

  &.isLoading {
    pointer-events: none;
    opacity: 0.7;
  }
}

.resendLinkContent {
  display: inline-flex;
  align-items: center;
  gap: $space-25;
}

.spinner {
  display: block;
  flex-shrink: 0;
  width: 1em;
  height: 1em;
  --theme-color-text-fill: var(--base-color-link);
}
</style>
